// Standing consents (FR-20, FR-22): which company may see which details, for
// which purposes and until when. Every change needs step-up and returns a
// signed receipt (FR-21).
import { Anchor, Button, Checkbox, Group, Loader, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { useStepUp } from "../../shared/session/stepup";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, Notice, WidePage } from "../../shared/ui/Page";

type Field = Schemas["ConsentField"];
type Company = Schemas["CompanySummary"];
type Consent = Schemas["Consent"];
type Receipt = Schemas["Receipt"];

const FIELDS: Field[] = ["email", "phone", "id_number", "name"];

export type ConsentDraft = { fields: Field[]; purposes: string; expires: string };

export function ConsentsPage() {
  return (
    <SignedIn>
      <Consents />
    </SignedIn>
  );
}

/** toBody turns the form into the PUT body, leaving out what was not set. */
export function toBody(d: ConsentDraft): { fields: Field[]; purposes?: string[]; expires_at?: string } {
  const purposes = d.purposes
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  return {
    fields: d.fields,
    ...(purposes.length ? { purposes } : {}),
    // A date means "until the end of that day" in the person's time zone.
    ...(d.expires ? { expires_at: new Date(`${d.expires}T23:59:59`).toISOString() } : {}),
  };
}

function draftOf(c: Consent | undefined): ConsentDraft {
  return {
    fields: c?.fields ?? [],
    purposes: c?.purposes.join(", ") ?? "",
    expires: c?.expires_at ? toDateInput(c.expires_at) : "",
  };
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function Consents() {
  const { t } = useTranslation();
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const consents = useQuery({ queryKey: ["consents"], queryFn: () => unwrap(api.GET("/consents")) });
  const [adding, setAdding] = useState<Company | "search" | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [result, setResult] = useState<{ receipt: Receipt; revoked: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = consents.data?.items ?? [];

  const changed = (receipt: Receipt, revoked: boolean) => {
    setResult({ receipt, revoked });
    setAdding(null);
    setEditing(null);
    void queryClient.invalidateQueries({ queryKey: ["consents"] });
    void queryClient.invalidateQueries({ queryKey: ["receipts"] });
  };

  const save = async (company: Company, draft: ConsentDraft): Promise<void> => {
    setError(null);
    setResult(null);
    const token = await stepUp("consent_change");
    if (!token) return;
    const { data, error } = await api.PUT("/consents/{company_id}", {
      params: { path: { company_id: company.id }, header: { "X-Step-Up-Token": token } },
      body: toBody(draft),
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    changed(data, false);
  };

  const revoke = async (c: Consent) => {
    setError(null);
    setResult(null);
    const token = await stepUp("consent_change");
    if (!token) return;
    const { data, error } = await api.DELETE("/consents/{company_id}", {
      params: { path: { company_id: c.company.id }, header: { "X-Step-Up-Token": token } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    changed(data, true);
  };

  return (
    <WidePage title={t("consents.title")} intro={t("consents.intro")}>
      {result && <ReceiptSummary receipt={result.receipt} revoked={result.revoked} />}
      <FormError message={error} />
      {adding === null ? (
        <Button onClick={() => setAdding("search")} w="fit-content">
          {t("consents.add")}
        </Button>
      ) : (
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Title order={2} size="h4">
              {t("consents.add")}
            </Title>
            {adding === "search" ? (
              <CompanyPicker onPick={setAdding} onCancel={() => setAdding(null)} />
            ) : (
              <>
                <Group justify="space-between">
                  <Text fw={600} data-testid="chosen-company">
                    {adding.name}
                  </Text>
                  <Anchor component="button" type="button" size="sm" onClick={() => setAdding("search")}>
                    {t("consents.change_company")}
                  </Anchor>
                </Group>
                <ConsentForm
                  key={adding.id}
                  initial={draftOf(items.find((c) => c.company.id === adding.id))}
                  onSubmit={(d) => save(adding, d)}
                  onCancel={() => setAdding(null)}
                />
              </>
            )}
          </Stack>
        </Paper>
      )}
      {consents.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : consents.isError ? (
        <FormError message={errorMessage(consents.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="consents-empty">
          {t("consents.empty")}
        </Text>
      ) : (
        <Stack gap="sm" component="ul" p={0} m={0} style={{ listStyle: "none" }} aria-label={t("consents.list")}>
          {items.map((c) => (
            <li key={c.company.id}>
              <ConsentCard
                consent={c}
                editing={editing === c.company.id}
                onEdit={() => setEditing(c.company.id)}
                onCancel={() => setEditing(null)}
                onSave={(d) => save(c.company, d)}
                onRevoke={() => revoke(c)}
              />
            </li>
          ))}
        </Stack>
      )}
    </WidePage>
  );
}

function ConsentCard(props: {
  consent: Consent;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (d: ConsentDraft) => Promise<void>;
  onRevoke: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const c = props.consent;
  return (
    <Paper withBorder p="md" radius="md" data-testid="consent">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2} size="h4">
              {c.company.name}
            </Title>
            <Text size="xs" c="dimmed">
              {t(`segment.${c.company.segment}`, { defaultValue: c.company.segment })}
            </Text>
          </Stack>
          <FieldBadges fields={c.fields} />
        </Group>
        <Text size="sm">
          {t("consents.purposes_label")}: {c.purposes.length ? c.purposes.join(", ") : t("consents.any_purpose")}
        </Text>
        <Text size="sm">
          {c.expires_at ? t("consents.expires_on", { date: f.date(c.expires_at) }) : t("consents.no_expiry")}
        </Text>
        <Text size="xs" c="dimmed">
          {t("consents.updated", { date: f.dateTime(c.updated_at) })}
        </Text>
        {props.editing ? (
          <ConsentForm initial={draftOf(c)} onSubmit={props.onSave} onCancel={props.onCancel} />
        ) : (
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              onClick={props.onEdit}
              aria-label={t("consents.edit_for", { company: c.company.name })}
            >
              {t("consents.edit")}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              onClick={() => void props.onRevoke()}
              aria-label={t("consents.revoke_for", { company: c.company.name })}
            >
              {t("consents.revoke")}
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function CompanyPicker({ onPick, onCancel }: { onPick: (c: Company) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);
  const companies = useQuery({
    queryKey: ["companies", debounced],
    queryFn: () =>
      unwrap(api.GET("/companies", { params: { query: { ...(debounced ? { search: debounced } : {}), limit: 10 } } })),
  });
  const list = companies.data?.items ?? [];
  return (
    <Stack gap="sm">
      <TextInput
        type="search"
        label={t("consents.search")}
        description={t("consents.search_hint")}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        data-autofocus
      />
      {companies.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : companies.isError ? (
        <FormError message={errorMessage(companies.error, t("error.generic"))} />
      ) : list.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("consents.no_results")}
        </Text>
      ) : (
        <Stack gap={4} component="ul" p={0} m={0} style={{ listStyle: "none" }} aria-label={t("consents.results")}>
          {list.map((c) => (
            <Group key={c.id} component="li" justify="space-between" wrap="nowrap" data-testid="company-result">
              <Stack gap={0}>
                <Text size="sm" fw={600}>
                  {c.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {t(`segment.${c.segment}`, { defaultValue: c.segment })}
                </Text>
              </Stack>
              <Button
                size="xs"
                variant="light"
                onClick={() => onPick(c)}
                aria-label={t("consents.choose_for", { company: c.name })}
              >
                {t("consents.choose")}
              </Button>
            </Group>
          ))}
        </Stack>
      )}
      <Button variant="default" onClick={onCancel} w="fit-content">
        {t("common.cancel")}
      </Button>
    </Stack>
  );
}

function ConsentForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: ConsentDraft;
  onSubmit: (d: ConsentDraft) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const [draft, setDraft] = useState<ConsentDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (draft.fields.length === 0) return setError(t("consents.pick_field"));
    setError(null);
    setBusy(true);
    try {
      await onSubmit(draft);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="consent-form">
      <Stack gap="sm">
        <Checkbox.Group
          label={t("consents.fields")}
          value={draft.fields}
          onChange={(v) => setDraft({ ...draft, fields: FIELDS.filter((x) => v.includes(x)) })}
        >
          <Stack gap={6} mt={6}>
            {FIELDS.map((x) => (
              <Checkbox key={x} value={x} label={f.field(x)} />
            ))}
          </Stack>
        </Checkbox.Group>
        <TextInput
          label={t("consents.purposes")}
          description={t("consents.purposes_hint")}
          value={draft.purposes}
          onChange={(e) => setDraft({ ...draft, purposes: e.currentTarget.value })}
        />
        <TextInput
          type="date"
          label={t("consents.expires")}
          description={t("consents.expires_hint")}
          value={draft.expires}
          min={toDateInput(new Date().toISOString())}
          onChange={(e) => setDraft({ ...draft, expires: e.currentTarget.value })}
        />
        <FormError message={error} />
        <Group gap="xs">
          <Button type="submit" loading={busy}>
            {t("consents.save")}
          </Button>
          <Button variant="default" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

function ReceiptSummary({ receipt, revoked }: { receipt: Receipt; revoked: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <Notice>
      <Stack gap={4} data-testid="receipt-summary">
        <Text size="sm" fw={600}>
          {revoked
            ? t("consents.revoked", { company: receipt.company.name })
            : t("consents.saved", { company: receipt.company.name, fields: f.fields(receipt.fields_after) })}
        </Text>
        <Text size="sm">
          {t("consents.change", { before: f.fields(receipt.fields_before), after: f.fields(receipt.fields_after) })}
        </Text>
        <Group gap="md">
          <Anchor component={Link} to={`/receipts/${receipt.event_id}`} size="sm">
            {t("consents.receipt_link")}
          </Anchor>
          <Anchor component={Link} to="/receipts" size="sm">
            {t("consents.all_receipts")}
          </Anchor>
        </Group>
      </Stack>
    </Notice>
  );
}
